import type { MappingFeature } from '@/entities/dashboard';
import type { LocationModel } from '@/entities/location';
import { MAP_REPORTS, type MapReport, type ReportValues } from '@/features/map-layers';
import type { ClusterSource } from '@/features/map-grouping';

export type ReportFeatures = Partial<Record<MapReport, MappingFeature[]>>;

/**
 * Value of one report at a point in time. The series the API returns is cumulative
 * and runs oldest week first, so "N weeks ago" is that many steps back from its end;
 * the whole history arrives in the first response, which is why moving the time
 * machine costs no request — legacy reads the same array the same way
 * (`mapping.js:257-270`).
 */
export function reportValueAt(feature: MappingFeature | undefined, weeksAgo: number): number {
  if (!feature) return 0;

  const index = feature.progress.length - 1 - weeksAgo;
  const value = index >= 0 && index < feature.progress.length ? feature.progress[index] : feature.totalValue;

  return Math.abs(value || 0);
}

/** Longest series any report returned — how far back the time machine may travel. */
export function weeksAvailable(features: ReportFeatures): number {
  return MAP_REPORTS.reduce(
    (weeks, report) => (features[report] ?? []).reduce((max, feature) => Math.max(max, feature.progress.length), weeks),
    0
  );
}

/**
 * Pairs every location with the value of each report at the selected week. A report
 * whose layer is off contributes zero rather than disappearing, exactly as legacy's
 * `isReportEnabled` gate does — the marker keeps its place, the slice goes away.
 */
export function buildClusterSources(
  locations: LocationModel[],
  features: ReportFeatures,
  enabled: MapReport[],
  weeksAgo: number
): ClusterSource[] {
  const byReport = new Map(
    MAP_REPORTS.map((report) => [report, new Map((features[report] ?? []).map((feature) => [feature.id, feature]))])
  );

  return locations.map((location) => {
    const values = Object.fromEntries(
      MAP_REPORTS.map((report) => [
        report,
        enabled.includes(report) ? reportValueAt(byReport.get(report)?.get(location.id), weeksAgo) : 0
      ])
    ) as ReportValues;

    return { location, values };
  });
}
